// frontend/src/pages/public/RoomDetailPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Clock, Users, Star, ChevronRight, MapPin, ArrowLeft } from 'lucide-react';
import BrandIcon from '../../components/BrandIcon';

function Stars({ value, max=5, size=16 }) {
  return (
    <div style={{ display:'flex', gap:3 }}>
      {Array.from({length:max},(_,i) => (
        <Star key={i} size={size}
          fill={i<value?'#f5a623':'none'}
          color={i<value?'#f5a623':'rgba(255,255,255,0.2)'}/>
      ))}
    </div>
  );
}

export default function RoomDetailPage() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const [room, setRoom]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessName, setBusiness] = useState('');

  useEffect(() => {
    api.get('/settings/public').then(r => setBusiness(r.data.settings?.business_name||'Escape Game')).catch(()=>{});
    api.get('/rooms/public/slug/'+slug)
      .then(r => setRoom(r.data.room))
      .catch(() => navigate('/salles'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex',
      alignItems:'center', justifyContent:'center', color:'var(--text-3)' }}>
      Chargement…
    </div>
  );

  if (!room) return null;

  const difficulty = room.difficulty || 0;
  const prices = (room.prices||[]).filter(Boolean);

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-1)' }}>
      {/* Header */}
      <div style={{ borderBottom:'1px solid var(--border)', padding:'16px 24px',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontWeight:800, fontSize:20, display:'flex', alignItems:'center', gap:8 }}>
          <BrandIcon size={22}/>
          <span style={{ background:'linear-gradient(135deg,var(--accent-light),var(--teal))',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>{businessName}</span>
        </div>
        <Link to="/salles" style={{ color:'var(--text-2)', fontSize:13, textDecoration:'none',
          display:'flex', alignItems:'center', gap:4 }}>
          <ArrowLeft size={14}/> Toutes les salles
        </Link>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'40px 24px' }}>
        {/* Image hero */}
        <div style={{ height:360, borderRadius:20, overflow:'hidden', marginBottom:32,
          background:'linear-gradient(135deg,#1a1a2e,#16213e)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:80 }}>
          {room.image_url
            ? <img src={room.image_url} alt={room.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <BrandIcon size={40}/>
          }
        </div>

        <div className="booking-grid" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:40, alignItems:'start' }}>
          {/* Infos principales */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
              {room.site_name && (
                <span style={{ fontSize:12, padding:'3px 10px', borderRadius:99,
                  background:`${room.site_color||'#7c5cfc'}22`, color:room.site_color||'var(--accent-light)',
                  fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                  <MapPin size={10}/> {room.site_name}
                </span>
              )}
              <Stars value={difficulty}/>
              <span style={{ fontSize:12, color:'var(--text-3)' }}>
                {['','Facile','Modéré','Difficile','Très difficile','Expert'][difficulty]||''}
              </span>
            </div>

            <h1 style={{ fontSize:'clamp(24px,4vw,36px)', fontWeight:800, marginBottom:16, lineHeight:1.2 }}>
              {room.name}
            </h1>

            {room.description && (
              <p style={{ fontSize:16, color:'var(--text-2)', lineHeight:1.7, marginBottom:24 }}>
                {room.description}
              </p>
            )}

            {room.story && (
              <div style={{ background:'var(--bg-elevated)', borderRadius:14, padding:24, marginBottom:24 }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:'var(--accent-light)',
                  textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
                  L'histoire
                </h3>
                <p style={{ fontSize:14, color:'var(--text-2)', lineHeight:1.8, fontStyle:'italic' }}>
                  {room.story}
                </p>
              </div>
            )}

            {/* Caractéristiques */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
              {[
                ['⏱', 'Durée', `${room.duration_min} min`],
                ['👥', 'Joueurs', `${room.min_players} à ${room.max_players}`],
                ['⭐', 'Difficulté', ['','Facile','Modéré','Difficile','Très difficile','Expert'][difficulty]||'—'],
              ].map(([icon, label, val]) => (
                <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:12,
                  padding:'16px 14px', textAlign:'center' }}>
                  <div style={{ fontSize:24, marginBottom:4 }}>{icon}</div>
                  <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:2 }}>{label}</div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Panneau réservation */}
          <div className="booking-sticky" style={{ position:'sticky', top:20 }}>
            <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)',
              borderRadius:16, padding:24 }}>
              <h3 style={{ fontSize:18, fontWeight:700, marginBottom:20 }}>Réserver</h3>

              {/* Tarifs */}
              {prices.length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, color:'var(--text-3)', fontWeight:600,
                    textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
                    Tarifs
                  </div>
                  {prices.map(p => {
                    const t0 = p.tiers?.[0];
                    if (!t0) return null;
                    return (
                      <div key={p.price_profile_id} style={{ display:'flex', justifyContent:'space-between',
                        alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                        <span style={{ fontSize:14, color:'var(--text-2)' }}>{p.profile_name}</span>
                        <span style={{ fontWeight:700, color:'var(--teal)', fontSize:14 }}>
                          {t0.price_flat
                            ? `${parseFloat(t0.price_flat).toFixed(0)} €`
                            : `dès ${parseFloat(t0.price_per_person).toFixed(0)} €/pers`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate(`/reserver?room=${room.id}`)}
                style={{ width:'100%', padding:'14px 0', borderRadius:10, fontSize:15, fontWeight:700,
                  background:'linear-gradient(135deg,var(--accent),var(--teal))', color:'#fff',
                  border:'none', cursor:'pointer', display:'flex', alignItems:'center',
                  justifyContent:'center', gap:8 }}>
                Réserver cette salle <ChevronRight size={16}/>
              </button>

              <p style={{ fontSize:11, color:'var(--text-3)', textAlign:'center', marginTop:12, lineHeight:1.5 }}>
                Réservation en ligne sécurisée<br/>Acompte requis à la réservation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
